import React, { useCallback, useMemo, useState } from "react";

import deepmerge from "deepmerge";
import Highcharts, {
  DrilldownEventObject,
  SeriesClickEventObject,
  TooltipFormatterContextObject,
  YAxisOptions,
  YAxisPlotLinesOptions,
} from "highcharts";
import HighchartsReact from "highcharts-react-official";
import drilldown from "highcharts/modules/drilldown.js";
import add from "lodash-es/add";
import { defineMessages, useIntl } from "react-intl";

import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";

import RottingDealsModal from "@app/component/DealListModalForCharts/RottingDealsModal";
import { DealsGroupByType } from "@app/scene/home/enum/DealsGroupByType";
import { DisplayType } from "@app/scene/home/enum/DisplayType";
import { DealsGroupBy } from "@app/scene/home/types/DealGroupBy";
import { RecordsStats } from "@app/scene/home/types/RecordsStats";
import RottingDealsModalData from "@app/types/ui/RottingDealsModalData";

import { displayTypeOperations, entityIdGetter, entityNameGetter } from "./util";

drilldown(Highcharts);

const DEFAULT_CHART_OPTIONS: Highcharts.Options = {
  chart: {
    type: "bar",
  },
  credits: {
    enabled: false,
  },
  legend: {
    enabled: false,
  },
  plotOptions: {
    column: {
      colorByPoint: true,
    },
  },
  title: {
    text: undefined,
  },
  tooltip: {
    headerFormat: "<small>{point.key}</small><br/>",
    useHTML: true,
  },
  xAxis: {
    labels: {
      style: {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },
  },
  yAxis: {
    title: undefined,
  },
};

const messages = defineMessages({
  relevant: {
    id: "home.cards.dealsRotting.chart.tooltip.relevant",
    defaultMessage: "Rotting deals",
    description: "Rotting Deals Card chart tooltip Records over cadence label",
  },
  relevantPercent: {
    id: "home.cards.dealsRotting.chart.tooltip.relevantPercent",
    defaultMessage: "Rot: {percent}%",
    description: "Rotting Deals Card chart tooltip Over cadence % label",
  },
  totalDeals: {
    id: "home.cards.dealsRotting.chart.tooltip.totalDeals",
    defaultMessage: "Total deals",
    description: "Rotting Deals Card chart tooltip Total records label",
  },
});

const yAxisLabelsFormat: Record<DisplayType, YAxisOptions["labels"]> = {
  [DisplayType.GROSS]: {
    format: "{value}",
  },
  [DisplayType.PERCENT]: {
    format: "{value}%",
  },
};

const CONTAINER_PROPS = {
  style: {
    width: "100%",
  },
};

const getStageKeyFromStageName = (stageName: string) => stageName.replace(/\s/g, "");

interface Props {
  displayType: DisplayType;
  funnelStages: Record<Funnel["id"], Stage[]>;
  groupByEntities: DealsGroupBy[];
  groupByType: DealsGroupByType;
  hideOption: (value: boolean) => void;
  records: Record<DealsGroupBy["id"], RecordsStats>;
  stagesRecords: Record<Funnel["id"], Record<Stage["name"], RecordsStats>>;
}

const Chart: React.FC<Props> = ({
  displayType,
  funnelStages,
  groupByEntities,
  groupByType,
  hideOption,
  records,
  stagesRecords,
}) => {
  const intl = useIntl();

  const [selectedFunnelId, setSelectedFunnelId] = useState<Funnel["id"] | undefined>(undefined);
  const stages = selectedFunnelId && funnelStages[selectedFunnelId];
  const stageKeyToStageMap = useMemo(
    () => stages && new Map(stages.map((stage) => [getStageKeyFromStageName(stage.name), stage])),
    [stages]
  );

  const [selectedData, setSelectedData] = useState<RottingDealsModalData | undefined>();
  const handleSelect = useCallback(
    (data) => {
      setSelectedData(data);
    },
    [setSelectedData]
  );

  const availableGroupByEntities = useMemo(
    () => groupByEntities.filter((entity: DealsGroupBy) => records[entity.id]),
    [groupByEntities, records]
  );

  const availableGroupByEntityIds = useMemo(
    () => groupByEntities.filter((entity: DealsGroupBy) => records[entity.id]),
    [groupByEntities, records]
  );

  const chartConfig = useMemo<Highcharts.Options>(() => {
    const selectedFunnelStages = stagesRecords[selectedFunnelId!];
    const stageKeys = selectedFunnelStages ? Object.keys(selectedFunnelStages) : [];
    const stageNames =
      stageKeyToStageMap && stageKeys.map((key) => stageKeyToStageMap.get(key)?.name);

    const names = availableGroupByEntities.map(entityNameGetter[groupByType]);
    const ids = availableGroupByEntityIds.map(entityIdGetter[groupByType]);

    const { calculate, round } = displayTypeOperations[displayType];
    const availableData = availableGroupByEntities
      .map((entity) => records[entity.id])
      .map(calculate);

    const seriesData = availableData.map((value, i) => {
      return {
        custom: {
          entityTypeId: ids[i],
        },
        drilldown: groupByType === DealsGroupByType.FUNNEL ? ids[i] : undefined,
        name: names[i],
        y: round(value),
      };
    });

    const drillDownData = selectedFunnelId
      ? [
          {
            id: selectedFunnelId,
            data:
              stageKeyToStageMap &&
              stageKeys
                .filter((stageKeys) => !!selectedFunnelStages[stageKeys])
                .map((stageKey) => {
                  return [
                    stageKeyToStageMap.get(stageKey)?.name ?? stageKey,
                    selectedFunnelStages[stageKey]
                      ? round(calculate(selectedFunnelStages[stageKey]))
                      : undefined,
                  ];
                }),
            name:
              (availableGroupByEntities as Funnel[]).find(({ id }) => id === selectedFunnelId)
                ?.name ?? "",
          },
        ]
      : [];

    const average = seriesData.length && availableData.reduce(add, 0) / seriesData.length;

    const averageFormatted = round(average);
    const plotLines: YAxisPlotLinesOptions[] = average
      ? [
          {
            color: "red",
            dashStyle: "LongDash",
            value: averageFormatted,
            width: 1,
            zIndex: 1,
          },
        ]
      : [];

    const result: Highcharts.Options = deepmerge.all([
      {},
      DEFAULT_CHART_OPTIONS,
      {
        accessibility: {
          announceNewData: {
            enabled: true,
          },
        },
        chart: {
          events: {
            drilldown: function (event: DrilldownEventObject) {
              const funnelId = event.point.options.custom?.entityTypeId;
              setSelectedFunnelId(funnelId);
              hideOption(true);
            },
            drillup: function () {
              hideOption(false);
            },
          },
          inverted: true,
          type: "column",
        },
        drilldown: {
          series: drillDownData,
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          column: {
            cursor: "pointer",
          },
          dataLabels: {
            enabled: true,
          },
          series: {
            events: {
              click: function (event: any) {
                const data = {
                  id: selectedFunnelId,
                  dealId: stageKeyToStageMap
                    ? stageKeyToStageMap.get(getStageKeyFromStageName(event.point.options.name))?.id
                    : undefined,
                  name: event.point.options.name,
                  type: groupByType,
                }; // click on a column
                handleSelect(data);
              },
            },
          },
        },
        series: [
          {
            data: seriesData,
            events: {
              click: function (event: SeriesClickEventObject) {
                const funnelId = event.point.options.custom?.entityTypeId;
                setSelectedFunnelId(funnelId);
                const data = {
                  id: event.point.options.custom?.entityTypeId,
                  name: event.point.options.name,
                  type: groupByType,
                }; // click on a column
                if (groupByType !== DealsGroupByType.FUNNEL) {
                  handleSelect(data);
                }
              },
            },
            name: "Funnels",
            type: "column",
          },
        ],
        tooltip: {
          formatter(this: TooltipFormatterContextObject): string {
            const { color, point } = this;
            // we know that drilldownLevels field exists
            // @ts-ignore
            if (this.series.chart?.drilldownLevels?.length > 0) {
              const realStageNames = stageNames && stageNames[point.index];
              const stageName = realStageNames ?? stageKeys[point.index];
              const stageKey = stageKeys[point.index];
              const totalDeals = selectedFunnelStages[stageKey]?.total;
              const relevantDeals = selectedFunnelStages[stageKey]?.relevant;
              const { calculate, round } = displayTypeOperations[DisplayType.PERCENT];
              const percent =
                selectedFunnelStages[stageKey] && round(calculate(selectedFunnelStages[stageKey]));
              return `<span style="color:${color};">${stageName}</span><br>
          <small>${intl.formatMessage(messages.totalDeals)}: ${totalDeals}</small><br>
          <small>${intl.formatMessage(messages.relevant)}: ${relevantDeals ?? undefined}</small><br>
          <small>${intl.formatMessage(messages.relevantPercent, { percent })}</small><br>`;
            } else {
              const availableGroupByEntity = availableGroupByEntities[point.index];
              const name = names[point.index];
              const groupedRecords = records[availableGroupByEntity.id];
              const { calculate, round } = displayTypeOperations[DisplayType.PERCENT];
              const percent = round(calculate(groupedRecords));
              return `<span style="color:${color};">${name}</span><br>
            <small>${intl.formatMessage(messages.totalDeals)}: ${groupedRecords.total}</small><br>
            <small>${intl.formatMessage(messages.relevant)}: ${groupedRecords.relevant}</small><br>
            <small>${intl.formatMessage(messages.relevantPercent, { percent })}</small><br>`;
            }
          },
        },
        xAxis: {
          type: "category",
        },
        yAxis: {
          labels: {
            ...yAxisLabelsFormat[displayType],
          },
          plotLines,
        },
      },
    ]);
    return result;
  }, [
    availableGroupByEntities,
    availableGroupByEntityIds,
    displayType,
    groupByType,
    handleSelect,
    hideOption,
    intl,
    records,
    selectedFunnelId,
    stageKeyToStageMap,
    stagesRecords,
  ]);

  return (
    <>
      <HighchartsReact
        containerProps={CONTAINER_PROPS}
        highcharts={Highcharts}
        isPureConfig
        key={groupByType}
        options={chartConfig}
      />
      {selectedData && (
        <RottingDealsModal data={selectedData} onHide={() => setSelectedData(undefined)} />
      )}
    </>
  );
};

export default React.memo(Chart);
